"use client";

import { useEffect, useState } from "react";
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import type {
  Issue,
  ProjectMilestone,
  ProjectMilestoneStatus,
} from "@/lib/types";
import { cn, formatDate } from "@/lib/utils";

export function ProjectMilestones({
  milestones,
  issues,
  canWrite,
  onCreate,
  onUpdate,
  onDelete,
}: {
  milestones: ProjectMilestone[];
  issues: Issue[];
  canWrite: boolean;
  onCreate: (input: { title: string; description: string; targetDate: number }) => Promise<unknown>;
  onUpdate: (id: string, patch: Partial<ProjectMilestone>) => Promise<unknown>;
  onDelete: (id: string) => Promise<unknown>;
}) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    if (!canWrite) return;
    if (!title.trim() || !targetDate) return;
    setSaving(true);
    setError(null);
    try {
      await onCreate({
        title: title.trim(),
        description: description.trim(),
        targetDate: parseDateInput(targetDate),
      });
      setTitle("");
      setDescription("");
      setTargetDate("");
      setAdding(false);
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "Could not add this goal.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)]">
      {milestones.length === 0 ? (
        <div className="flex min-h-[180px] items-center justify-center px-6 text-center">
          <div className="max-w-md">
            <div className="mx-auto mb-3 flex h-9 w-9 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--bg-tertiary)] text-[var(--fg-tertiary)]">
              <Calendar size={15} />
            </div>
            <p className="text-[12.5px] font-medium text-[var(--fg)]">No dated goals yet</p>
            <p className="mt-1 text-[11.5px] leading-relaxed text-[var(--fg-tertiary)]">
              Add the concrete A/B/C/D outcomes that lead to the project finish line.
            </p>
            {canWrite && (
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-2.5 py-1.5 text-[11.5px] font-medium text-white hover:bg-[var(--accent-hover)] transition-colors"
              >
                <Plus size={11} /> Add first goal
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="relative grid gap-0 md:grid-cols-2 xl:grid-cols-4">
          <span className="absolute left-[12.5%] right-[12.5%] top-[26px] hidden h-px bg-[var(--border-strong)] xl:block" />
          {milestones.map((milestone, index) => (
            <MilestoneCard
              key={milestone.id}
              milestone={milestone}
              label={milestoneLabel(index)}
              issues={issues}
              canWrite={canWrite}
              onUpdate={(patch) => onUpdate(milestone.id, patch)}
              onDelete={() => onDelete(milestone.id)}
            />
          ))}
        </div>
      )}

      {canWrite && adding ? (
        <div className="border-t border-[var(--border)] bg-[var(--bg-tertiary)] p-3">
          <div className="grid gap-2 lg:grid-cols-[minmax(180px,1fr)_minmax(220px,1.5fr)_140px_auto]">
            <input
              autoFocus
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Goal, e.g. Live beta"
              className="h-8 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-2.5 text-[11.5px] outline-none placeholder:text-[var(--fg-muted)] focus:border-[var(--accent)]"
            />
            <input
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Observable outcome"
              className="h-8 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-2.5 text-[11.5px] outline-none placeholder:text-[var(--fg-muted)] focus:border-[var(--accent)]"
            />
            <input
              type="date"
              value={targetDate}
              onChange={(event) => setTargetDate(event.target.value)}
              className="h-8 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-2 text-[11px] text-[var(--fg-secondary)] outline-none focus:border-[var(--accent)]"
            />
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => void create()}
                disabled={saving || !title.trim() || !targetDate}
                className="h-8 rounded-md bg-[var(--accent)] px-3 text-[11.5px] font-medium text-white hover:bg-[var(--accent-hover)] disabled:opacity-40 transition-colors"
              >
                {saving ? "Saving…" : "Add goal"}
              </button>
              <button
                type="button"
                onClick={() => setAdding(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)] transition-colors"
                aria-label="Cancel"
              >
                <X size={12} />
              </button>
            </div>
          </div>
          {error && <p className="mt-2 text-[10.5px] text-[var(--red)]">{error}</p>}
        </div>
      ) : canWrite && milestones.length > 0 ? (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex w-full items-center justify-center gap-1.5 border-t border-[var(--border)] px-3 py-2 text-[11px] font-medium text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg)] transition-colors"
        >
          <Plus size={11} /> Add dated goal
        </button>
      ) : null}
    </div>
  );
}

function MilestoneCard({
  milestone,
  label,
  issues,
  canWrite,
  onUpdate,
  onDelete,
}: {
  milestone: ProjectMilestone;
  label: string;
  issues: Issue[];
  canWrite: boolean;
  onUpdate: (patch: Partial<ProjectMilestone>) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
}) {
  const linked = issues.filter((issue) => milestone.linkedIssueIds.includes(issue.id));
  const done = linked.filter((issue) => issue.status === "done").length;
  const progress = linked.length ? Math.round((done / linked.length) * 100) : null;
  const overdue =
    milestone.status !== "achieved" &&
    startOfDay(milestone.targetDate) < startOfDay(Date.now());
  const effectiveStatus = overdue ? "overdue" : milestone.status;
  const available = issues.filter((issue) => !milestone.linkedIssueIds.includes(issue.id));
  const [writeError, setWriteError] = useState<string | null>(null);

  async function save(patch: Partial<ProjectMilestone>) {
    if (!canWrite) return false;
    setWriteError(null);
    try {
      await onUpdate(patch);
      return true;
    } catch (updateError) {
      setWriteError(updateError instanceof Error ? updateError.message : "Could not update this goal.");
      return false;
    }
  }

  return (
    <div className="relative min-w-0 border-b border-[var(--border)] p-3 last:border-b-0 md:border-r md:[&:nth-child(2n)]:border-r-0 xl:border-b-0 xl:[&:nth-child(2n)]:border-r xl:last:border-r-0">
      <div className="relative z-[1] mb-3 flex items-center gap-2">
        <span
          className="flex h-7 w-7 items-center justify-center rounded-full border-2 bg-[var(--bg-secondary)] text-[10px] font-medium"
          style={{ borderColor: milestoneTone(effectiveStatus), color: milestoneTone(effectiveStatus) }}
        >
          {milestone.status === "achieved" ? <CheckCircle2 size={12} /> : label}
        </span>
        <div>
          <div className="text-[10px] font-medium uppercase tracking-wider text-[var(--fg-muted)]">Goal {label}</div>
          <div className="text-[10.5px] text-[var(--fg-tertiary)]">{formatDate(milestone.targetDate)}</div>
        </div>
        <StatusBadge status={effectiveStatus} />
      </div>

      {canWrite ? (
        <>
          <EditableText
            value={milestone.title}
            onSave={(title) => save({ title })}
            aria-label={`Goal ${label} title`}
            className="w-full bg-transparent text-[12.5px] font-medium text-[var(--fg)] outline-none focus:text-[var(--accent)]"
          />
          <EditableText
            value={milestone.description}
            onSave={(description) => save({ description })}
            allowEmpty
            placeholder="Observable outcome"
            aria-label={`Goal ${label} description`}
            className="mt-1 w-full bg-transparent text-[10.5px] text-[var(--fg-tertiary)] outline-none placeholder:text-[var(--fg-muted)] focus:text-[var(--fg-secondary)]"
          />
        </>
      ) : (
        <>
          <p className="text-[12.5px] font-medium text-[var(--fg)]">{milestone.title}</p>
          {milestone.description && (
            <p className="mt-1 text-[10.5px] text-[var(--fg-tertiary)]">
              {milestone.description}
            </p>
          )}
        </>
      )}

      <div className="mt-3">
        <div className="mb-1.5 flex items-center justify-between text-[10px] text-[var(--fg-muted)]">
          <span>{linked.length ? `${done}/${linked.length} linked tasks done` : "No work linked yet"}</span>
          {progress !== null && <span>{progress}%</span>}
        </div>
        <div className="h-1 overflow-hidden rounded-full bg-[var(--bg-tertiary)]">
          <span
            className="block h-full rounded-full"
            style={{ width: `${progress ?? 0}%`, background: milestoneTone(effectiveStatus) }}
          />
        </div>
      </div>

      {linked.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {linked.map((issue) => (
            <span
              key={issue.id}
              className="inline-flex items-center gap-1 rounded bg-[var(--bg-tertiary)] px-1.5 py-1 font-mono text-[9.5px] text-[var(--fg-tertiary)]"
              title={issue.title}
            >
              {issue.identifier}
              {canWrite && (
                <button
                  type="button"
                  onClick={() =>
                    void save({
                      linkedIssueIds: milestone.linkedIssueIds.filter((id) => id !== issue.id),
                    })
                  }
                  className="rounded text-[var(--fg-muted)] hover:text-[var(--red)] transition-colors"
                  aria-label={`Unlink ${issue.identifier}`}
                >
                  <X size={8} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {canWrite && (
      <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-[var(--border)] pt-2.5">
        <select
          value={milestone.status}
          onChange={(event) =>
            void save({ status: event.target.value as ProjectMilestoneStatus })
          }
          aria-label={`Goal ${label} status`}
          className="h-7 rounded border border-[var(--border)] bg-[var(--bg-tertiary)] px-1.5 text-[10px] text-[var(--fg-secondary)] outline-none hover:bg-[var(--bg-hover)] transition-colors"
        >
          <option value="planned">Planned</option>
          <option value="in_progress">In progress</option>
          <option value="at_risk">At risk</option>
          <option value="achieved">Achieved</option>
        </select>
        <input
          type="date"
          value={toDateInput(milestone.targetDate)}
          onChange={(event) => {
            if (event.target.value) {
              void save({ targetDate: parseDateInput(event.target.value) });
            }
          }}
          aria-label={`Goal ${label} target date`}
          className="h-7 w-[112px] rounded border border-[var(--border)] bg-[var(--bg-tertiary)] px-1.5 text-[10px] text-[var(--fg-secondary)] outline-none hover:bg-[var(--bg-hover)] transition-colors"
        />
        <select
          value=""
          disabled={available.length === 0}
          onChange={(event) => {
            if (!event.target.value) return;
            void save({ linkedIssueIds: [...milestone.linkedIssueIds, event.target.value] });
          }}
          aria-label={`Link work to goal ${label}`}
          className="h-7 min-w-0 flex-1 rounded border border-[var(--border)] bg-[var(--bg-tertiary)] px-1.5 text-[10px] text-[var(--fg-secondary)] outline-none hover:bg-[var(--bg-hover)] disabled:opacity-40 transition-colors"
        >
          <option value="">Link work…</option>
          {available.map((issue) => (
            <option key={issue.id} value={issue.id}>{issue.identifier} · {issue.title}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => {
            if (confirm(`Delete goal ${label}: ${milestone.title}?`)) {
              void onDelete().catch((deleteError) =>
                setWriteError(
                  deleteError instanceof Error ? deleteError.message : "Could not delete this goal."
                )
              );
            }
          }}
          className="flex h-7 w-7 items-center justify-center rounded text-[var(--fg-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--red)] transition-colors"
          aria-label={`Delete goal ${label}`}
        >
          <Trash2 size={10} />
        </button>
      </div>
      )}
      {writeError && <p className="mt-2 text-[10px] text-[var(--red)]">{writeError}</p>}
    </div>
  );
}

function EditableText({
  value,
  onSave,
  allowEmpty = false,
  className,
  ...props
}: {
  value: string;
  onSave: (value: string) => Promise<boolean>;
  allowEmpty?: boolean;
  className: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "onBlur">) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  async function commit() {
    const next = draft.trim();
    if (next === value || (!allowEmpty && !next)) {
      setDraft(value);
      return;
    }
    try {
      const saved = await onSave(next);
      if (!saved) setDraft(value);
    } catch {
      setDraft(value);
    }
  }

  return (
    <input
      {...props}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
        if (event.key === "Escape") {
          setDraft(value);
          event.currentTarget.blur();
        }
      }}
      className={className}
    />
  );
}

function StatusBadge({ status }: { status: ProjectMilestoneStatus | "overdue" }) {
  const labels = {
    planned: "Planned",
    in_progress: "Moving",
    at_risk: "At risk",
    achieved: "Achieved",
    overdue: "Overdue",
  };
  return (
    <span
      className={cn(
        "ml-auto inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9.5px] font-medium",
        (status === "at_risk" || status === "overdue") && "text-[var(--red)]"
      )}
      style={{
        color: milestoneTone(status),
        background: `color-mix(in srgb, ${milestoneTone(status)} 12%, transparent)`,
      }}
    >
      {(status === "at_risk" || status === "overdue") && <AlertTriangle size={8} />}
      {labels[status]}
    </span>
  );
}

function milestoneLabel(index: number) {
  return index < 26 ? String.fromCharCode(65 + index) : String(index + 1);
}

function milestoneTone(status: ProjectMilestoneStatus | "overdue") {
  if (status === "achieved") return "var(--green)";
  if (status === "at_risk" || status === "overdue") return "var(--red)";
  if (status === "in_progress") return "var(--blue)";
  return "var(--fg-muted)";
}

function toDateInput(value: number) {
  const date = new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function parseDateInput(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 12).getTime();
}

function startOfDay(value: number) {
  const date = new Date(value);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}
